import isNil from 'lodash/isNil';
import sortBy from 'lodash/sortBy';
import uniqBy from 'lodash/uniqBy';

import {
  CalendarDate,
  createCalendarDate,
  endOfCalendarMonth,
  fromCalendarDate,
  getLocaleFromLanguage,
  getNumberOfMonthsBetweenCalendarDates,
  Interval,
  MONTHS_IN_YEAR,
  startOfCalendarMonth,
} from '../dateutils';
import { isNilOrEmpty } from '../objectUtils';
import { Language } from '../types';

const MIN_MONTH_NAME_LENGTH = 3;
const MONTH_NAME_FORMATS = ['long', 'short'] as const;
const NAME_REFERENCE_YEAR = 2000;

const STRUCTURED_COMMUNICATION_REGEX = /(\+{3}|\*{3})[\d/\s]+(\+{3}|\*{3})/g;
const MONTH_THEN_YEAR_REGEX = /\b(0?[1-9]|1[0-2])[/.-](20\d{2})\b/g;
const YEAR_THEN_MONTH_REGEX = /\b(20\d{2})-(0[1-9]|1[0-2])\b/g;
const DAY_MONTH_YEAR_REGEX = /\b(?:0?[1-9]|[12]\d|3[01])[/.-](0?[1-9]|1[0-2])[/.-](20\d{2}|\d{2})\b/g;
const TWO_DIGIT_YEAR_CENTURY = 2000;
const TWO_DIGIT_YEAR_LENGTH = 2;
const MAX_TWO_DIGIT_YEAR_DISTANCE = 1;

const parseFullDateMonths = (text: string, referenceDate: CalendarDate): CalendarDate[] =>
  [...text.matchAll(DAY_MONTH_YEAR_REGEX)].reduce<CalendarDate[]>((months, [, month, year]) => {
    if (year.length !== TWO_DIGIT_YEAR_LENGTH) {
      months.push(createCalendarDate(Number(year), Number(month), 1));
      return months;
    }
    const fullYear = TWO_DIGIT_YEAR_CENTURY + Number(year);
    if (Math.abs(fullYear - referenceDate.year) <= MAX_TWO_DIGIT_YEAR_DISTANCE) {
      months.push(createCalendarDate(fullYear, Number(month), 1));
    }
    return months;
  }, []);

const normalizeText = (text: string): string => text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

const escapeRegex = (text: string): string => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const buildMonthNumberByName = (): Map<string, number> =>
  Object.values(Language).reduce<Map<string, number>>((monthNumberByName, language) => {
    const { code } = getLocaleFromLanguage(language);
    Array.from({ length: MONTHS_IN_YEAR }, (_, index) => index + 1).forEach((month) => {
      MONTH_NAME_FORMATS.forEach((format) => {
        const name = normalizeText(
          createCalendarDate(NAME_REFERENCE_YEAR, month, 1).toLocaleString(code, { month: format })
        ).replace(/\./g, '');
        if (name.length >= MIN_MONTH_NAME_LENGTH) {
          monthNumberByName.set(name, month);
        }
      });
    });
    return monthNumberByName;
  }, new Map<string, number>());

let monthNameMatcher: { monthNumberByName: Map<string, number>; regex: RegExp } | undefined;

const getMonthNameMatcher = () => {
  if (isNil(monthNameMatcher)) {
    const monthNumberByName = buildMonthNumberByName();
    const alternatives = sortBy([...monthNumberByName.keys()], (name) => -name.length).map(escapeRegex);
    monthNameMatcher = {
      monthNumberByName,
      regex: new RegExp(`\\b(${alternatives.join('|')})\\.?(?:\\s+(20\\d{2}))?\\b`, 'g'),
    };
  }
  return monthNameMatcher;
};

const resolveYearlessMonth = (month: number, referenceDate: CalendarDate): CalendarDate => {
  const referenceMonth = startOfCalendarMonth(referenceDate);
  const candidates = [-1, 0, 1].map((yearOffset) => createCalendarDate(referenceDate.year + yearOffset, month, 1));
  return sortBy(candidates, (candidate) =>
    Math.abs(getNumberOfMonthsBetweenCalendarDates(referenceMonth, candidate))
  )[0];
};

const parseOneCommunication = (communication: string, referenceDate: CalendarDate): CalendarDate[] => {
  const text = normalizeText(communication).replace(STRUCTURED_COMMUNICATION_REGEX, ' ');
  const { monthNumberByName, regex } = getMonthNameMatcher();
  const namedMonths = [...text.matchAll(regex)].reduce<CalendarDate[]>((months, [, name, year]) => {
    const month = monthNumberByName.get(name);
    if (isNil(month)) {
      return months;
    }
    months.push(isNil(year) ? resolveYearlessMonth(month, referenceDate) : createCalendarDate(Number(year), month, 1));
    return months;
  }, []);
  const monthThenYear = [...text.matchAll(MONTH_THEN_YEAR_REGEX)].map(([, month, year]) =>
    createCalendarDate(Number(year), Number(month), 1)
  );
  const yearThenMonth = [...text.matchAll(YEAR_THEN_MONTH_REGEX)].map(([, year, month]) =>
    createCalendarDate(Number(year), Number(month), 1)
  );
  return [...namedMonths, ...monthThenYear, ...yearThenMonth, ...parseFullDateMonths(text, referenceDate)];
};

export const parseCitedMonths = (
  communications: (string | null | undefined)[],
  referenceDate: CalendarDate
): CalendarDate[] => {
  const months = communications.reduce<CalendarDate[]>((allMonths, communication) => {
    if (isNilOrEmpty(communication)) {
      return allMonths;
    }
    return [...allMonths, ...parseOneCommunication(communication, referenceDate)];
  }, []);
  return sortBy(uniqBy(months, fromCalendarDate), fromCalendarDate);
};

export const getCitedMonthInterval = (month: CalendarDate): Interval => ({
  start: month,
  end: endOfCalendarMonth(month),
});
